import { yupResolver } from "@hookform/resolvers/yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import AddressForm from "components/common/AddressForm";
import ContactInfoForm from "components/common/ContactInfoForm";
import type { UpdatePracticeBranchDto, BranchDto } from "libs/cellera-sdk";
import { useCallback, useState, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { branchSchema, type BranchFormData } from "validations";

interface UpdateBranchModalProps {
  readonly open: boolean;
  readonly branch: BranchDto | null;
  readonly onClose: () => void;
  readonly onUpdate: (
    branchId: string,
    data: UpdatePracticeBranchDto
  ) => Promise<void>;
}

function UpdateBranchModal({
  open,
  branch,
  onClose,
  onUpdate,
}: UpdateBranchModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm({
    resolver: yupResolver(branchSchema),
    defaultValues: {
      address: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      emailAddress: "",
      primaryPhoneNumber: "",
      faxNumber: "",
    },
    mode: "onChange",
  });

  // Populate form when branch data changes
  useEffect(() => {
    if (branch && open) {
      methods.reset({
        address: branch.address || "",
        street: branch.street || "",
        city: branch.city || "",
        state: branch.state || "",
        zip: branch.zip || "",
        emailAddress: branch.emailAddress || "",
        primaryPhoneNumber: branch.primaryPhoneNumber || "",
        faxNumber: branch.faxNumber || "",
      });
    }
  }, [branch, open, methods]);

  const handleSubmit = useCallback(
    async (data: BranchFormData) => {
      if (!branch?.id) return;

      try {
        setIsSubmitting(true);
        await onUpdate(branch.id, data as UpdatePracticeBranchDto);
        onClose();
      } catch (error) {
        console.error("Error updating branch:", error);
        // Error toast handled by parent
      } finally {
        setIsSubmitting(false);
      }
    },
    [branch?.id, onUpdate, onClose]
  );

  const handleClose = useCallback(() => {
    methods.reset();
    onClose();
  }, [onClose, methods]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Update Branch</DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogContent>
            <Box className="space-y-6">
              {/* Address Section */}
              <Box>
                <Typography variant="subtitle1" className="font-medium mb-4">
                  Address
                </Typography>
                <AddressForm />
              </Box>

              {/* Contact Information Section */}
              <Box>
                <Typography variant="subtitle1" className="font-medium mb-4">
                  Contact Information
                </Typography>
                <ContactInfoForm />
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!methods.formState.isValid || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? "Updating..." : "Update"}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default UpdateBranchModal;
